import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import Button from './ui/Button';
import Alert from './ui/Alert';
import { useToast } from '../context/ToastContext';
import { isValidCnp } from '../utils/validation';

const CNPModal = ({ onClose, onLater, onSuccess }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [cnp, setCnp] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValidCnp(cnp)) {
      setError(cnp.length !== 13 ? t('modals.cnp.mustBe13') : t('employees.valCNPInvalid'));
      return;
    }

    setError(null);
    setSaving(true);

    try {
      await api.put('/employees/me', { cnp });
      showToast(t('modals.cnp.saved'), 'success');
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (err) {
      setError(err.response?.data?.error || t('toasts.errorGeneric'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-sm">
        <div className="modal-header">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary-100 dark:bg-primary-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-5 h-5 text-primary-600 dark:text-primary-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {t('modals.cnp.title')}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('modals.cnp.subtitle')}
                </p>
              </div>
            </div>
            {/* Buton X — închidere/amânare ca să nu blocheze platforma (ex: useri fără CNP încă) */}
            {(onLater || onClose) && (
              <button
                type="button"
                onClick={onLater || onClose}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-xl leading-none flex-shrink-0 p-1 -mt-1"
                aria-label={t('common.close')}
                title={t('common.later')}
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className="modal-body">
          {error && (
            <Alert type="error" className="mb-3">
              {error}
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-4" id="cnp-form">
            <div>
              <label htmlFor="cnp" className="input-label">
                CNP *
              </label>
              <input
                id="cnp"
                type="text"
                required
                maxLength={13}
                pattern="[0-9]{13}"
                value={cnp}
                onChange={(e) => setCnp(e.target.value.replace(/\D/g, ''))}
                className="input font-mono text-lg"
                placeholder="1234567890123"
                inputMode="numeric"
              />
              <p className="input-helper flex items-center gap-1.5">
                <svg
                  className="w-3.5 h-3.5 text-slate-400 dark:text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                {t('modals.cnp.gdprNote')}
              </p>
              <div className="mt-1 text-right">
                <span
                  className={`text-xs font-mono ${cnp.length === 13 ? 'text-accent-600 dark:text-accent-300' : 'text-slate-400 dark:text-slate-400'}`}
                >
                  {cnp.length}/13
                </span>
              </div>
            </div>
          </form>
        </div>

        <div className="modal-footer">
          {(onLater || onClose) && (
            <Button type="button" variant="secondary" onClick={onLater || onClose}>
              {t('common.later')}
            </Button>
          )}
          <Button
            type="submit"
            form="cnp-form"
            loading={saving}
            disabled={!isValidCnp(cnp)}
            className="flex-1"
          >
            {saving ? t('common.loading') : t('modals.cnp.saveBtn')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CNPModal;
